import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { HeartPulse, AlertOctagon, AlertTriangle } from 'lucide-react';

// Polls the lightweight Data Health summary endpoint every 5 minutes and
// shows a small icon button in the topbar with a coloured badge when the
// most recent scan found errors or warnings. Click jumps to the Data Health
// admin tab via URL hash deep-link.
//
// Admin / Business Head only — RBAC mirrored from the backend.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

export const DataHealthAlert = () => {
  const { currentUser, api } = useApp();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const isAdmin = currentUser && ['admin', 'business_head'].includes(currentUser.role);

  const fetchSummary = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.getDataHealthSummary();
      setSummary(res.data);
    } catch (e) {
      // Silent — topbar alert is best-effort, don't surface errors here
    } finally {
      setLoaded(true);
    }
  }, [api, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    fetchSummary();
    const id = setInterval(fetchSummary, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAdmin, fetchSummary]);

  if (!isAdmin || !loaded) return null;
  if (!summary?.ready) {
    // Backend hasn't run a scan yet — render a neutral icon (clickable so admin
    // can manually open the tab even before the daily loop fires its first scan)
    return (
      <button
        type="button"
        data-testid="data-health-alert"
        title="Data Health — no scan yet"
        onClick={() => navigate('/admin#data-health')}
        className="relative p-2 rounded-lg hover:bg-slate-50 transition-colors"
      >
        <HeartPulse className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
      </button>
    );
  }

  const { error = 0, warn = 0 } = summary.summary || {};
  const hasError = error > 0;
  const hasWarn = !hasError && warn > 0;
  const isClean = !hasError && !hasWarn;

  const tone = hasError
    ? { icon: AlertOctagon, color: 'text-red-600', bg: 'bg-red-500', label: `${error} data health error${error === 1 ? '' : 's'}` }
    : hasWarn
      ? { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-500', label: `${warn} data health warning${warn === 1 ? '' : 's'}` }
      : { icon: HeartPulse, color: 'text-emerald-500', bg: '', label: 'Data Health: all clean' };

  const TipIcon = tone.icon;

  return (
    <button
      type="button"
      data-testid="data-health-alert"
      title={`${tone.label}\nLast scan: ${summary.checked_at ? new Date(summary.checked_at).toLocaleString() : '—'}\nClick to open Data Health tab`}
      onClick={() => navigate('/admin#data-health')}
      className="relative p-2 rounded-lg hover:bg-slate-50 transition-colors"
    >
      <TipIcon className={`w-5 h-5 ${tone.color}`} strokeWidth={1.5} />
      {!isClean && (
        <span
          data-testid="data-health-alert-badge"
          className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${tone.bg} ring-2 ring-white`}
        >
          {hasError ? error : warn}
        </span>
      )}
    </button>
  );
};
